/* eslint-disable */
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Chip,
  IconButton,
  Button,
  Stack,
  Grid,
  Divider,
  CircularProgress,
  Collapse,
  Tooltip,
} from '@mui/material'
import {
  LocationOn as MapPinIcon,
  Bed as BedIcon,
  Bathtub as BathIcon,
  DirectionsCar as CarIcon,
  OpenWith as MoveIcon,
  Phone as PhoneIcon,
  Email as MailIcon,
  Visibility as EyeIcon,
  PlayCircleOutline as PlayCircleIcon,
  Favorite as HeartIcon,
  FavoriteBorder as HeartOutlineIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material'
import { PROPERTY_PLACEHOLDER_IMAGE, getPropertyImage, handleImageError, convertGoogleDriveVideoUrl } from '../Constants/images'
import { formatPrice, formatArea } from '../Utils/formatters'
import { buildPropertyImageAlt } from '../Utils/propertyImageAlt'
import { trackEvent } from '../Utils/analytics'
import { savedPropertyAPI } from '../services/api'
import { useToast } from '../context/ToastContext'
import { fetchFileById } from '../Api/request/propertyDetail'
import UseAxios from '../Api/http/http'

function PropertyListMedia({
  property,
  hasImages,
  hasVideo,
  isLoggedIn,
  initialImageUrl,
  firstVideoUrl,
  firstImageFileId,
  firstVideoFileId,
  getBlobUrlByFileId,
  imagePriority = false,
}) {
  const [isImageResolving, setIsImageResolving] = useState(false)
  const [isImageLoading, setIsImageLoading] = useState(false)
  const [resolvedImageUrl, setResolvedImageUrl] = useState(initialImageUrl)
  const [resolvedVideoUrl, setResolvedVideoUrl] = useState(firstVideoUrl)
  const [imageBlobUrl, setImageBlobUrl] = useState(null)
  const [videoBlobUrl, setVideoBlobUrl] = useState(null)
  const imageFallbackAttempted = useRef(false)
  const videoFallbackAttempted = useRef(false)

  useEffect(() => {
    const missingImageUrl =
      !initialImageUrl || initialImageUrl === PROPERTY_PLACEHOLDER_IMAGE
    const shouldFetchImageById = Boolean(firstImageFileId) && missingImageUrl
    const staticImageUrl = initialImageUrl

    setResolvedImageUrl(shouldFetchImageById ? null : staticImageUrl)
    setResolvedVideoUrl(firstVideoUrl)
    setIsImageResolving(shouldFetchImageById)
    setIsImageLoading(Boolean(!shouldFetchImageById && staticImageUrl && staticImageUrl !== PROPERTY_PLACEHOLDER_IMAGE))
    imageFallbackAttempted.current = false
    videoFallbackAttempted.current = false
  }, [initialImageUrl, firstVideoUrl, firstImageFileId])

  useEffect(() => {
    return () => {
      if (imageBlobUrl) URL.revokeObjectURL(imageBlobUrl)
      if (videoBlobUrl) URL.revokeObjectURL(videoBlobUrl)
    }
  }, [imageBlobUrl, videoBlobUrl])

  useEffect(() => {
    let active = true

    const loadImageById = async () => {
      const missingImageUrl =
        !initialImageUrl || initialImageUrl === PROPERTY_PLACEHOLDER_IMAGE
      const shouldFetchById = missingImageUrl
      if (!shouldFetchById || !firstImageFileId || imageFallbackAttempted.current) return

      imageFallbackAttempted.current = true
      try {
        const blobUrl = await getBlobUrlByFileId(firstImageFileId, 'image/jpeg')
        if (!active || !blobUrl) {
          if (active) setIsImageResolving(false)
          return
        }
        if (imageBlobUrl) URL.revokeObjectURL(imageBlobUrl)
        setImageBlobUrl(blobUrl)
        setResolvedImageUrl(blobUrl)
        setIsImageLoading(true)
        setIsImageResolving(false)
      } catch (error) {
        console.error('Image proactive fallback fetch failed', error)
        if (active) setIsImageResolving(false)
      }
    }

    const loadVideoById = async () => {
      if (firstVideoUrl || !firstVideoFileId || videoFallbackAttempted.current) return

      videoFallbackAttempted.current = true
      try {
        const blobUrl = await getBlobUrlByFileId(firstVideoFileId, 'video/mp4')
        if (!active || !blobUrl) return
        if (videoBlobUrl) URL.revokeObjectURL(videoBlobUrl)
        setVideoBlobUrl(blobUrl)
        setResolvedVideoUrl(blobUrl)
      } catch (error) {
        console.error('Video proactive fallback fetch failed', error)
      }
    }

    loadImageById()
    loadVideoById()

    return () => {
      active = false
    }
  }, [
    initialImageUrl,
    firstImageFileId,
    firstVideoUrl,
    firstVideoFileId,
    imageBlobUrl,
    videoBlobUrl,
    getBlobUrlByFileId,
  ])

  if (hasImages && isImageResolving) {
    return (
      <Box
        sx={{
          width: '100%',
          height: 240,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'grey.100',
          color: 'text.secondary',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            color: '#6b7280',
            fontSize: 12,
          }}
        >
          <CircularProgress size={24} thickness={4} sx={{ color: '#1976d2' }} />
          <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>
            Loading image...
          </Typography>
        </Box>
      </Box>
    )
  }

  if (hasImages) {
    return (
      <Box sx={{ position: 'relative', width: '100%', height: 240 }}>
        <CardMedia
          component="img"
          image={resolvedImageUrl || PROPERTY_PLACEHOLDER_IMAGE}
          alt={buildPropertyImageAlt(property, { variant: 'listing' })}
          onLoad={() => setIsImageLoading(false)}
          onError={async (event) => {
            if (event.currentTarget.dataset.fallbackAttempted === '1') {
              setIsImageLoading(false)
              handleImageError(event)
              return
            }

            event.currentTarget.dataset.fallbackAttempted = '1'
            setResolvedImageUrl(null)
            setIsImageResolving(true)

            if (!firstImageFileId) {
              setIsImageResolving(false)
              setIsImageLoading(false)
              handleImageError(event)
              return
            }

            try {
              const blobUrl = await getBlobUrlByFileId(firstImageFileId, 'image/jpeg')
              if (blobUrl) {
                event.currentTarget.src = blobUrl
                event.currentTarget.dataset.blobUrl = blobUrl
                setResolvedImageUrl(blobUrl)
                setIsImageLoading(true)
                setIsImageResolving(false)
                return
              }
            } catch (error) {
              console.error('Image fallback fetch failed', error)
            }

            setIsImageResolving(false)
            setIsImageLoading(false)

            handleImageError(event)
          }}
          loading={imagePriority ? 'eager' : 'lazy'}
          fetchPriority={imagePriority ? 'high' : 'auto'}
          decoding='async'
          referrerPolicy="no-referrer"
          sx={{ width: '100%', height: 240, objectFit: 'cover' }}
        />

        {isImageLoading && !isImageResolving && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.45)',
              zIndex: 1,
            }}
          >
            <CircularProgress size={24} thickness={4} sx={{ color: '#1976d2' }} />
          </Box>
        )}
      </Box>
    )
  }

  if (hasVideo) {
    return (
      <Box
        component="video"
        src={resolvedVideoUrl || firstVideoUrl}
        muted
        controls
        playsInline
        preload="metadata"
        onError={async (event) => {
          if (event.currentTarget.dataset.fallbackAttempted === '1' || !firstVideoFileId) return
          event.currentTarget.dataset.fallbackAttempted = '1'

          try {
            const blobUrl = await getBlobUrlByFileId(firstVideoFileId, 'video/mp4')
            if (blobUrl) {
              event.currentTarget.src = blobUrl
              event.currentTarget.dataset.blobUrl = blobUrl
            }
          } catch (error) {
            console.error('Video fallback fetch failed', error)
          }
        }}
        sx={{ width: '100%', height: 240, objectFit: 'cover', backgroundColor: 'black' }}
      />
    )
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: 240,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'grey.100',
        color: 'text.secondary',
        fontWeight: 500,
      }}
    >
      No image available
    </Box>
  )
}

export function PropertyListView({
  properties,
  onViewDetails,
  isLoggedIn = false,
  user = null,
  showEditDelete = false,
  onEdit,
  onDelete,
  onUnlist,
  savedPropertyIds = [],
  onSaveToggle,
  hideViewDetailsForDraft = false,
  useUpdateAsSecondaryAction = false,
  onUpdateVerification,
  imagePriorityCount = 0,
}) {
  const toast = useToast()
  const navigate = useNavigate()
  const location = useLocation()
  const [savingPropertyId, setSavingPropertyId] = useState(null)
  const [openContactPropertyId, setOpenContactPropertyId] = useState('')

  const handleToggleSave = async (e, propertyId) => {
    e.stopPropagation()

    if (!isLoggedIn) {
      toast.showInfo('Please login to save properties')
      return
    }

    setSavingPropertyId(propertyId)
    const isSaved = savedPropertyIds.includes(propertyId)

    try {
      const selectedProperty = properties.find((item) => String(item.id) === String(propertyId))
      const trackedPropertyType = String(
        selectedProperty?.propertyType || selectedProperty?.property_type || ''
      )

      if (isSaved) {
        await savedPropertyAPI.unsaveProperty(propertyId)
        toast.showSuccess('Property removed from saved')
        trackEvent('remove_from_wishlist', {
          item_id: String(propertyId),
          item_category: trackedPropertyType,
          page_path: `${location.pathname}${location.search}`,
        })
      } else {
        await savedPropertyAPI.saveProperty(propertyId)
        toast.showSuccess('Property saved successfully')
        trackEvent('add_to_wishlist', {
          item_id: String(propertyId),
          item_category: trackedPropertyType,
          page_path: `${location.pathname}${location.search}`,
        })
      }

      if (onSaveToggle) {
        onSaveToggle(propertyId, !isSaved)
      }
    } catch (error) {
      console.error('Error toggling save:', error)
      toast.showError(error.response?.data?.message || 'Failed to update saved status')
    } finally {
      setSavingPropertyId(null)
    }
  }

  const getProp = (property, camelCaseKey, snakeCaseKey, defaultValue = '') => {
    return property[camelCaseKey] !== undefined
      ? property[camelCaseKey]
      : (property[snakeCaseKey] !== undefined ? property[snakeCaseKey] : defaultValue)
  }

  const getFirstValue = (...values) => {
    for (const value of values) {
      if (value !== null && value !== undefined && String(value).trim() !== '') {
        return value
      }
    }
    return ''
  }

  const getPropertyIdentifier = (property) =>
    String(property.property_id || property.propertyId || property.id || '')

  useEffect(() => {
    const pendingContactPropertyId = location.state?.openContactPropertyId
    if (!isLoggedIn || !pendingContactPropertyId) return

    setOpenContactPropertyId(String(pendingContactPropertyId))
    navigate(location.pathname + location.search, {
      replace: true,
      state: {},
    })
  }, [isLoggedIn, location.pathname, location.search, location.state, navigate])

  const handleContactSeller = (property) => {
    const propertyIdentifier = getPropertyIdentifier(property)

    if (!isLoggedIn) {
      navigate('/login', {
        state: {
          from: location.pathname + location.search,
          openContactPropertyId: propertyIdentifier,
        },
      })
      return
    }

    setOpenContactPropertyId((prev) => (prev === propertyIdentifier ? '' : propertyIdentifier))
  }

  const parseArray = (value) => {
    if (!value) return []
    if (Array.isArray(value)) return value
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value)
        return Array.isArray(parsed) ? parsed : []
      } catch (e) {
        return []
      }
    }
    if (typeof value === 'object') {
      if (Array.isArray(value.data)) return value.data
      if (Array.isArray(value.items)) return value.items
      if (Array.isArray(value.files)) return value.files
      if (Array.isArray(value.receiptData)) return value.receiptData
    }
    return []
  }

  const toMediaUrl = (item) => {
    if (!item) return null
    if (typeof item === 'string') return convertGoogleDriveVideoUrl(item)
    return convertGoogleDriveVideoUrl(item.preview || item.uploadUrl || item.gcsUrl || item.url || item.path || null)
  }

  const isLikelyUrl = (value) => {
    if (!value || typeof value !== 'string') return false
    return (
      value.startsWith('http://') ||
      value.startsWith('https://') ||
      value.startsWith('/') ||
      value.startsWith('data:') ||
      value.startsWith('blob:')
    )
  }

  const pickFirstNonEmptyArray = (...candidates) => {
    for (const candidate of candidates) {
      const parsed = parseArray(candidate)
      if (parsed.length > 0) return parsed
    }
    return []
  }

  const getMediaFileId = (item) => {
    if (!item) return ''

    const extractFileIdFromMediaUrl = (value) => {
      if (!value || typeof value !== 'string') return ''

      const queryMatch = value.match(/[?&](?:id|fileId|file_id)=([a-zA-Z0-9_-]+)/i)
      if (queryMatch?.[1]) return queryMatch[1]

      const pathMatch = value.match(/\/(?:images|videos)\/([a-zA-Z0-9_-]+)(?:[/?#]|$)/i)
      if (pathMatch?.[1]) return pathMatch[1]

      const driveMatch = value.match(/\/d\/([a-zA-Z0-9_-]+)(?:[/?#]|$)/i)
      if (driveMatch?.[1]) return driveMatch[1]

      return ''
    }

    if (typeof item === 'string') {
      return isLikelyUrl(item) ? extractFileIdFromMediaUrl(item) : item
    }

    if (typeof item !== 'object') return ''
    const value =
      item.id ||
      item.fileId ||
      item.file_id ||
      item.image_id ||
      item.imageId ||
      item.video_id ||
      item.videoId ||
      item.property_image_id ||
      item.propertyImageId ||
      item.media_id ||
      item.mediaId ||
      item.drive_file_id ||
      item.driveFileId ||
      extractFileIdFromMediaUrl(item.preview || item.uploadUrl || item.gcsUrl || item.url || item.path || '')
    return value ? String(value) : ''
  }

  const getBlobUrlByFileId = async (fileId, fallbackType) => {
    if (!fileId) return null
    const response = await UseAxios(fetchFileById({ fileId }))
    if (!response?.data) return null

    const blob = response.data instanceof Blob
      ? response.data
      : response.data instanceof ArrayBuffer
      ? new Blob([response.data], { type: fallbackType })
      : null

    if (!blob) return null
    return URL.createObjectURL(blob)
  }

  return (
    <Stack spacing={3}>
      {properties.map((property, index) => {
        // Check if the logged-in user is the property owner
        const isPropertyOwner = isLoggedIn && user && property.user_id && (parseInt(property.user_id) === parseInt(user.id));
        const imageArray = parseArray(property.images)
        const propertyImageMetaArray = pickFirstNonEmptyArray(
          property.property_images,
          property.propertyImages
        )
        const videoArray = pickFirstNonEmptyArray(
          property.videos,
          property.property_videos,
          property.propertyVideos
        )
        const firstVideoUrl = toMediaUrl(videoArray[0])
        const initialImageUrl = getPropertyImage(property, (camel, snake, def) => getProp(property, camel, snake, def))
        const firstImageFileId =
          getMediaFileId(propertyImageMetaArray[0]) ||
          getMediaFileId(imageArray[0]) ||
          getMediaFileId(property.image) ||
          (property.image_id ? String(property.image_id) : '') ||
          (property.property_image_id ? String(property.property_image_id) : '')
        const firstVideoFileId = getMediaFileId(videoArray[0])
        const hasImages = imageArray.length > 0 || propertyImageMetaArray.length > 0 || Boolean(property.property_image_id)
        const hasVideo = Boolean(firstVideoUrl || firstVideoFileId)
        const propertyIdentifier = getPropertyIdentifier(property)
        const showContactDetails = openContactPropertyId === propertyIdentifier
        const sellerType = String(getFirstValue(property.sellingAs, property.selling_as, 'Owner')).toLowerCase()
        const isBrokerSeller = sellerType === 'broker'
        // const sellerName = isBrokerSeller
        //   ? getFirstValue(property.broker, property.broker_name)
        //   : getFirstValue(property.ownerName, property.owner_name)
        // const sellerPhone = isBrokerSeller
        //   ? getFirstValue(property.brokerContact, property.broker_contact, property.broker_phone_number)
        //   : getFirstValue(property.ownerContact, property.owner_contact, property.owner_phone_number)
        const sellerName = "Prakash"
        const sellerPhone = "9900872434"
        const sellerEmail = isBrokerSeller
          ? getFirstValue(property.brokerEmail, property.broker_email_id, property.broker_email)
          : getFirstValue(property.ownerEmail, property.owner_email_id, property.owner_email)
        const cardDisplayPrice = getFirstValue(
          property.estimatedCurrentValue,
          property.estimated_current_value,
          property.expectedPrice,
          property.expected_price,
          0
        )
        const rawLandmark = getFirstValue(property.landmark)
        const displayLandmark =
          typeof rawLandmark === 'string' && rawLandmark.trim().toLowerCase() === 'location not specified'
            ? ''
            : rawLandmark
        const displayDistrict = getFirstValue(property.district)
        const locationText = [displayLandmark, displayDistrict].filter(Boolean).join(', ')

        return (
        <Card
          key={`${property.id}-${index}`}
          sx={{
            overflow: 'hidden',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 6
            }
          }}
        >
          <Grid container>
            {/* Property Image */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{ position: 'relative', height: 240, cursor: 'pointer' }}
                onClick={() => !(hideViewDetailsForDraft && property.isDraft) && onViewDetails(property)}
              >
                <PropertyListMedia
                  property={property}
                  hasImages={hasImages}
                  hasVideo={hasVideo}
                  isLoggedIn={isLoggedIn}
                  initialImageUrl={initialImageUrl}
                  firstVideoUrl={firstVideoUrl}
                  firstImageFileId={firstImageFileId}
                  firstVideoFileId={firstVideoFileId}
                  getBlobUrlByFileId={getBlobUrlByFileId}
                  imagePriority={index < imagePriorityCount}
                />
                <Box sx={{ position: 'absolute', top: 12, left: 12, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {property.featured && (
                    <Chip
                      label="Featured"
                      size="small"
                      sx={{
                        bgcolor: '#ff6b35',
                        color: 'white',
                        fontWeight: 600
                      }}
                    />
                  )}
                  {imageArray.length > 0 && (
                      <Chip
                        label={`${imageArray.length} Photos`}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(255, 255, 255, 0.9)',
                          color: 'text.primary',
                          fontWeight: 500
                        }}
                      />
                  )}
                  {videoArray.length > 0 && (
                    <Chip
                      icon={<PlayCircleIcon sx={{ fontSize: '16px !important' }} />}
                      label={`${videoArray.length} Videos`}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        color: 'text.primary',
                        fontWeight: 500
                      }}
                    />
                  )}
                </Box>
                {showEditDelete ? (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      display: 'flex',
                      gap: 1,
                    }}
                  >
                    <Tooltip title='Edit Property' arrow>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation()
                          onEdit && onEdit(property)
                        }}
                        size='small'
                        sx={{
                          bgcolor: 'rgba(255, 255, 255, 0.95)',
                          boxShadow: 2,
                          '&:hover': {
                            bgcolor: 'white'
                          },
                          transition: 'all 0.2s ease-in-out',
                        }}
                      >
                        <EditIcon fontSize='small' color='primary' />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Delete Property' arrow>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation()
                          onDelete && onDelete(property)
                        }}
                        size='small'
                        sx={{
                          bgcolor: 'rgba(255, 255, 255, 0.95)',
                          boxShadow: 2,
                          '&:hover': {
                            bgcolor: 'white'
                          },
                          transition: 'all 0.2s ease-in-out',
                        }}
                      >
                        <DeleteIcon fontSize='small' color='error' />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Unlist Property' arrow>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation()
                          onUnlist && onUnlist(property)
                        }}
                        size='small'
                        sx={{
                          bgcolor: 'rgba(255, 255, 255, 0.95)',
                          boxShadow: 2,
                          '&:hover': {
                            bgcolor: 'white'
                          },
                          transition: 'all 0.2s ease-in-out',
                        }}
                      >
                        <VisibilityOffIcon fontSize='small' color='primary' />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : (
                  isLoggedIn && (
                    <IconButton
                      onClick={(e) => handleToggleSave(e, property.id)}
                      disabled={savingPropertyId === property.id}
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        '&:hover': {
                          bgcolor: 'white'
                        },
                        color: savedPropertyIds.includes(property.id) ? 'error.main' : 'text.secondary',
                        transition: 'all 0.2s ease-in-out'
                      }}
                      size='small'
                    >
                      {savingPropertyId === property.id ? (
                        <CircularProgress size={20} />
                      ) : savedPropertyIds.includes(property.id) ? (
                        <HeartIcon fontSize='small' />
                      ) : (
                        <HeartOutlineIcon fontSize='small' />
                      )}
                    </IconButton>
                  )
                )}
              </Box>
            </Grid>

            {/* Property Details */}
            <Grid item xs={12} md={8}>
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 3 }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" component="h3" sx={{ fontWeight: 600, fontSize: '1.1rem', mb: 1, textAlign: 'left', alignSelf: 'flex-start' }}>
                        {property.propertyType || property.property_type || 'Property'} - {property.propertySubType || property.property_sub_type || ''}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <MapPinIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                          {locationText || 'Location not specified'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: 'primary.main',
                          fontSize: '1.25rem',
                          mb: 0.5
                        }}
                      >
                        {formatPrice(cardDisplayPrice)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(() => {
                          const perCent = property.perCentPrice || property.per_cent_price;
                          const perSqft = property.perSqftPrice || property.per_sqft_price;
                          if (perCent && Number(perCent) > 0) return `${perCent} Lakhs/Cent`;
                          if (perSqft && Number(perSqft) > 0) return `₹${perSqft}/sq ft`;
                          return null;
                        })()}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                    {property.address}
                  </Typography>

                  {/* Property Features */}
                  <Stack direction="row" spacing={3} sx={{ mb: 3, flexWrap: 'wrap' }}>
                    {(property.bedroom_count || property.bedrooms || 0) > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <BedIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {property.bedroom_count || property.bedrooms} Beds
                        </Typography>
                      </Box>
                    )}
                    {property.bathrooms > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <BathIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {property.bathrooms} Baths
                        </Typography>
                      </Box>
                    )}
                    {property.parking > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CarIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {property.parking} Parking
                        </Typography>
                      </Box>
                    )}
                    {property.carpetArea && property.carpetArea > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <MoveIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {property.carpetArea.toLocaleString()} sq ft
                        </Typography>
                      </Box>
                    )}
                  </Stack>

                  {/* Property Features Tags */}
                  <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap' }}>
                    <Chip
                      label={property.propertySubType}
                      size="small"
                      variant="outlined"
                      sx={{ borderColor: 'primary.main', color: 'primary.main' }}
                    />
                    {property.electricity && (
                      <Chip label="Electricity" size="small" variant="outlined" />
                    )}
                    {property.waterSource && property.waterSource !== 'None' && (
                      <Chip label="Water Supply" size="small" variant="outlined" />
                    )}
                  </Stack>
                </Box>

                {/* Contact Information */}
                <Box sx={{ mt: 'auto' }}>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box />
                    {isPropertyOwner ? (
                      useUpdateAsSecondaryAction ? (
                        <Stack direction="row" spacing={1}>
                          {!(hideViewDetailsForDraft && property.isDraft) && (
                            <Button
                              variant="outlined"
                              startIcon={<EyeIcon />}
                              onClick={() => onViewDetails(property)}
                              sx={{ textTransform: 'none' }}
                            >
                              View Details
                            </Button>
                          )}
                          <Button
                            variant="contained"
                            onClick={() => onUpdateVerification && onUpdateVerification(property)}
                            sx={{ textTransform: 'none' }}
                          >
                            Update
                          </Button>
                        </Stack>
                      ) : (
                        // Hide View Details button for draft properties if hideViewDetailsForDraft is true
                        !(hideViewDetailsForDraft && property.isDraft) && (
                          <Button
                            variant="outlined"
                            fullWidth
                            startIcon={<EyeIcon />}
                            onClick={() => onViewDetails(property)}
                            sx={{ textTransform: 'none' }}
                          >
                            View Details
                          </Button>
                        )
                      )
                    ) : (
                      <Stack direction="row" spacing={1}>
                        {/* Hide View Details button for draft properties if hideViewDetailsForDraft is true */}
                        {!(hideViewDetailsForDraft && property.isDraft) && (
                          <Button
                            variant="outlined"
                            startIcon={<EyeIcon />}
                            onClick={() => onViewDetails(property)}
                            sx={{ textTransform: 'none' }}
                          >
                            View Details
                          </Button>
                        )}
                        {useUpdateAsSecondaryAction ? (
                          <Button
                            variant="contained"
                            onClick={() => onUpdateVerification && onUpdateVerification(property)}
                            sx={{ textTransform: 'none' }}
                          >
                            Update
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            startIcon={<PhoneIcon />}
                            onClick={() => handleContactSeller(property)}
                            sx={{ textTransform: 'none' }}
                          >
                            {showContactDetails ? 'Hide Contact' : 'Contact'}
                          </Button>
                        )}
                      </Stack>
                    )}
                  </Box>

                  {!isPropertyOwner && !useUpdateAsSecondaryAction && (
                    <Collapse in={showContactDetails}>
                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          bgcolor: 'grey.50',
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'grey.200',
                        }}
                      >
                        {!sellerName && !sellerEmail && !sellerPhone ? (
                          <Typography variant="body2" color="text.secondary">
                            No contact information available
                          </Typography>
                        ) : (
                          <Stack spacing={0.75}>
                            {sellerName && (
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {sellerName}
                              </Typography>
                            )}
                            {sellerEmail && (
                              <Typography
                                component="a"
                                href={`mailto:${sellerEmail}`}
                                variant="body2"
                                sx={{
                                  color: 'primary.main',
                                  textDecoration: 'none',
                                  fontWeight: 500,
                                  '&:hover': {
                                    textDecoration: 'underline',
                                  },
                                }}
                              >
                                {sellerEmail}
                              </Typography>
                            )}
                            {sellerPhone && (
                              <Typography
                                component="a"
                                href={`tel:${sellerPhone}`}
                                variant="body2"
                                sx={{
                                  color: 'primary.main',
                                  textDecoration: 'none',
                                  fontWeight: 500,
                                  '&:hover': {
                                    textDecoration: 'underline',
                                  },
                                }}
                              >
                                {sellerPhone}
                              </Typography>
                            )}
                          </Stack>
                        )}
                      </Box>
                    </Collapse>
                  )}
                </Box>
              </CardContent>
            </Grid>
          </Grid>
        </Card>
        )
      })}
    </Stack>
  )
}